/**
 * Chunk-ref diffing between two versions of a grid (docs/large-volume-plan.md §2.1 gridDiff.ts).
 *
 * Shared refs are skipped, so the cost is O(changed chunks) between versions of one lineage. When
 * lineage is broken (two independently built grids over the same palette) every differing chunk ref
 * costs one 4096-cell compare. Grids with different palettes are 'incomparable'.
 */
import type { VoxelData } from '@/voxelTypes'
import { CHUNK_CELLS, LEAF_ENTRIES, LINEAR_TO_MORTON12, TOP_ENTRIES, keyOf } from './layout'

// Module-local alias: bundlers inline it, and vitest's SSR transform would otherwise turn the
// imported binding into a getter call per cell inside the hot compare loops below.
const CELLS = CHUNK_CELLS
import type { VoxelChunk } from './chunk'
import type { ChunkedVoxelGrid } from './ChunkedVoxelGrid'
import type { VoxelPalette } from './palette'

export type ChunkDelta = {
  chunkId: number
  prev: VoxelChunk | null
  next: VoxelChunk | null
  /** 6 bits: a changed cell lies on the chunk face -x (1), +x (2), -y (4), +y (8), -z (16), +z (32). */
  boundaryMask: number
}

/** Structurally identical to `VoxelMutation` in events/mutationJournal (storage must not import events). */
export type CellMutation = { key: number; previous?: VoxelData; next?: VoxelData }

export const BOUNDARY_NEG_X = 1
export const BOUNDARY_POS_X = 2
export const BOUNDARY_NEG_Y = 4
export const BOUNDARY_POS_Y = 8
export const BOUNDARY_NEG_Z = 16
export const BOUNDARY_POS_Z = 32

export function diffGrids(a: ChunkedVoxelGrid, b: ChunkedVoxelGrid): ChunkDelta[] | 'incomparable' {
  if (a.palette !== b.palette) return 'incomparable'
  const out: ChunkDelta[] = []
  if (a === b) return out
  const la = a.leaves, lb = b.leaves
  for (let t = 0; t < TOP_ENTRIES; t++) {
    const leafA = la[t], leafB = lb[t]
    if (leafA === leafB) continue
    const ca = leafA ? leafA.chunks : null
    const cb = leafB ? leafB.chunks : null
    for (let s = 0; s < LEAF_ENTRIES; s++) {
      const prev = ca ? ca[s] : null
      const next = cb ? cb[s] : null
      if (prev === next) continue
      const mask = boundaryMaskOf(prev, next)
      if (mask < 0) continue // identical content behind different refs
      out.push({ chunkId: (t << 9) | s, prev, next, boundaryMask: mask })
    }
  }
  return out
}

/**
 * Compares two chunk refs cell by cell. Returns -1 when no cell differs, else the 6-bit boundary mask
 * of the faces touched by changed cells.
 */
function boundaryMaskOf(prev: VoxelChunk | null, next: VoxelChunk | null): number {
  const pd = prev ? prev.data : null
  const nd = next ? next.data : null
  if (pd === null && nd === null) return -1
  if ((pd === null && next!.count === 0) || (nd === null && prev!.count === 0)) return -1
  let mask = 0
  let any = false
  for (let i = 0; i < CELLS; i++) {
    const p = pd === null ? 0 : pd[i]
    const n = nd === null ? 0 : nd[i]
    if (p === n) continue
    any = true
    const lx = i & 15, ly = (i >> 4) & 15, lz = i >> 8
    if (lx === 0) mask |= BOUNDARY_NEG_X
    else if (lx === 15) mask |= BOUNDARY_POS_X
    if (ly === 0) mask |= BOUNDARY_NEG_Y
    else if (ly === 15) mask |= BOUNDARY_POS_Y
    if (lz === 0) mask |= BOUNDARY_NEG_Z
    else if (lz === 15) mask |= BOUNDARY_POS_Z
    if (mask === 63) break
  }
  return any ? mask : -1
}

/** Calls `fn` for every cell whose palette id differs between prev and next, across all deltas. */
export function forEachCellChange(
  deltas: readonly ChunkDelta[],
  fn: (key: number, prevId: number, nextId: number) => void,
): void {
  for (const d of deltas) {
    const pd = d.prev ? d.prev.data : null
    const nd = d.next ? d.next.data : null
    const chunkId = d.chunkId
    for (let i = 0; i < CELLS; i++) {
      const p = pd === null ? 0 : pd[i]
      const n = nd === null ? 0 : nd[i]
      if (p !== n) fn(keyOf(chunkId, i), p, n)
    }
  }
}

// Local alias: imported bindings are live getter accesses under Vite's SSR transform (see
// ChunkedVoxelGrid.ts), which matters in the per-cell loop below.
const L2M = LINEAR_TO_MORTON12

/** Expands chunk deltas into per-cell mutations (legacy consumers only; see plan §6). */
export function expandCellMutations(deltas: readonly ChunkDelta[], palette: VoxelPalette): CellMutation[] {
  const items = palette.items
  const out: CellMutation[] = []
  for (const d of deltas) {
    const pd = d.prev ? d.prev.data : null
    const nd = d.next ? d.next.data : null
    const keyBase = d.chunkId << 12
    for (let i = 0; i < CELLS; i++) {
      const p = pd === null ? 0 : pd[i]
      const n = nd === null ? 0 : nd[i]
      if (p === n) continue
      const m: CellMutation = { key: keyBase | L2M[i] }
      if (p !== 0) m.previous = items[p]
      if (n !== 0) m.next = items[n]
      out.push(m)
    }
  }
  return out
}

/** Number of changed cells across deltas (without allocating mutations). */
export function countCellChanges(deltas: readonly ChunkDelta[]): number {
  let n = 0
  forEachCellChange(deltas, () => { n++ })
  return n
}
